'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { RecordingCountdown } from '@/components/transcribe/RecordingCountdown';
import { TranscriptionEngine } from '@/components/transcribe/TranscriptionEngine';
import { TranscriptionMinutesAutogen } from '@/components/transcribe/TranscriptionMinutesAutogen';
import { Button } from '@/components/ui/button';
import {
  type TranscriptionRecordingSessionState,
  useTranscriptionRecordingSession,
} from '@/hooks/useTranscriptionRecordingSession';
import { formatDuration } from '@/lib/time';

const MAX_RECORDING_DURATION_MS = (() => {
  const raw = process.env.NEXT_PUBLIC_TRANSCRIPTION_MAX_RECORDING_MS;
  if (!raw) return 3 * 60 * 60 * 1000;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 3 * 60 * 60 * 1000;
  return parsed;
})();

type TickerStatus = 'recording' | 'processing' | 'connecting' | 'error';

const STATUS_LABEL: Record<TickerStatus, string> = {
  recording: '録音中',
  processing: '処理中',
  connecting: '接続中',
  error: 'エラー',
};

function resolveStatus(session: TranscriptionRecordingSessionState): TickerStatus | null {
  if (session.isRecording || session.engineState === 'recording') return 'recording';
  if (session.isProcessing || session.engineState === 'processing') return 'processing';
  if (session.engineState === 'connecting') return 'connecting';
  if (session.engineState === 'error') return 'error';
  return null;
}

function resolveDotClass(status: TickerStatus): string {
  if (status === 'processing') return 'bg-amber-500 animate-pulse';
  if (status === 'error') return 'bg-red-500';
  return 'bg-slate-400';
}

function isTranscribeDetailPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  // startsWith catches both `/transcribe` and `/transcribe/<messageId>` (catch-all route),
  // as well as `/planner/audio` and its detail path.
  return pathname.startsWith('/transcribe') || pathname.startsWith('/planner/audio');
}

function SoundBar() {
  return (
    <div className="soundbar-container shrink-0" aria-hidden="true">
      {[0, 0.2, 0.4].map((delay, i) => (
        <span
          key={i}
          className="soundbar-bar bg-red-500"
          style={{ animationDelay: `${delay}s` }}
        />
      ))}
    </div>
  );
}

/** ティッカー表示（純粋なステータス表示のみ） */
function TranscriptionTicker() {
  const pathname = usePathname();
  const router = useRouter();
  const session = useTranscriptionRecordingSession();
  const [elapsedNowMs, setElapsedNowMs] = useState(() => Date.now());

  const status = resolveStatus(session);
  const isRecording = status === 'recording';

  const elapsedLabel = useMemo(() => {
    if (!session.recordingStartedAtMs) return null;
    return formatDuration(elapsedNowMs - session.recordingStartedAtMs);
  }, [elapsedNowMs, session.recordingStartedAtMs]);

  useEffect(() => {
    if (!session.isRecording) return;
    const id = window.setInterval(() => { setElapsedNowMs(Date.now()); }, 1000);
    return () => { window.clearInterval(id); };
  }, [session.isRecording]);

  if (status === null) return null;
  // Planner 配下では FloatingRecorderFab が右下で録音状態を示すため、左下の Ticker を抑制
  if (pathname?.startsWith('/planner')) return null;

  const isRecordingPage = isTranscribeDetailPath(pathname);
  const showTabLabel = session.useTabAudio && session.selectedTabLabel;

  return (
    <div className="fixed bottom-3 left-3 z-50 w-[min(260px,calc(100vw-1.5rem))]">
      <div className="rounded-xl border border-slate-200 bg-white/90 p-2 shadow-lg backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isRecording ? (
                <SoundBar />
              ) : (
                <span
                  className={`h-2.5 w-2.5 rounded-full shrink-0 ${resolveDotClass(status)}`}
                  aria-hidden="true"
                />
              )}
              <div className="truncate text-xs font-semibold text-slate-900">
                {STATUS_LABEL[status]}
                {elapsedLabel ? (
                  <>
                    <span className="ml-2 text-xs font-normal text-slate-500">{elapsedLabel}</span>
                    {isRecording ? (
                      <RecordingCountdown
                        recordingStartedAtMs={session.recordingStartedAtMs}
                        maxRecordingDurationMs={MAX_RECORDING_DURATION_MS}
                      />
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
            {session.statusMessage ? (
              <div className="mt-1 truncate text-[11px] text-slate-600">{session.statusMessage}</div>
            ) : null}
            {showTabLabel ? (
              <div className="mt-1 truncate text-[10px] text-slate-500">入力先: {session.selectedTabLabel}</div>
            ) : null}
          </div>

          {!isRecordingPage ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => { router.push('/planner/audio'); }}
            >
              開く
            </Button>
          ) : null}
        </div>

        {session.error ? <div className="mt-2 text-xs text-red-600">{session.error}</div> : null}
      </div>
    </div>
  );
}

/** エンジン + ティッカーをまとめてマウント */
export function TranscriptionRecordingSystem() {
  return (
    <>
      <TranscriptionEngine />
      <TranscriptionMinutesAutogen />
      <TranscriptionTicker />
    </>
  );
}
